'use client';

import { Fragment } from 'react';
import type { ToolPageData, ToolPageSection } from '../types';
import { AccordionPanel as CategoryTabsAccordion } from './CategoryTabsSection/AccordionPanel';
import { TabSwitch as CategoryTabsSwitch } from './CategoryTabsSection/TabSwitch';
import { ClosingCTASection } from './ClosingCTASection';
import { ComparisonTableSection } from './ComparisonTableSection';
import { FAQSection } from './FAQSection';
import { FeatureHighlightsSection } from './FeatureHighlightsSection';
import { FeatureIntroSection } from './FeatureIntroSection';
import { CenteredHero } from './HeroSection/CenteredHero';
import { ImmersiveHero } from './HeroSection/ImmersiveHero';
import { ProductStyleHero } from './HeroSection/ProductStyleHero';
import { SplitLayoutHero } from './HeroSection/SplitLayoutHero';
import { HowToSection } from './HowToSection';
import { OfficialShowcaseSection } from './OfficialShowcaseSection';
import { PromptFormulaSection } from './PromptFormulaSection';
import { RelatedFeaturesSection } from './RelatedFeaturesSection';
import { RelatedScenariosSection } from './RelatedScenariosSection';
import { TestimonialsSection } from './TestimonialsSection';
import { CardGrid as UseCaseCardsGrid } from './UseCaseCardsSection/CardGrid';
import { SplitList as UseCaseCardsSplitList } from './UseCaseCardsSection/SplitList';
import { Alternating as UseCaseIntroAlternating } from './UseCaseIntroSection/Alternating';
import { TabPreview as UseCaseIntroTabPreview } from './UseCaseIntroSection/TabPreview';
import { Carousel as UserCasesCarousel } from './UserCasesSection/Carousel';
import { MasonryGrid as UserCasesMasonry } from './UserCasesSection/MasonryGrid';

function renderSection(section: ToolPageSection) {
  switch (section._type) {
    // 首屏：沉浸式输入(default) / 居中无输入 / 产品展示 / 左右分栏
    case 'hero':
      switch (section.layout) {
        case 'centered_hero':
          return <CenteredHero section={section} />;
        case 'product_style':
          return <ProductStyleHero section={section} />;
        case 'split_layout':
          return <SplitLayoutHero section={section} />;
        default:
          return <ImmersiveHero section={section} />;
      }
    // 功能介绍
    case 'feature_intro':
      return <FeatureIntroSection section={section} />;
    // 具体步骤
    case 'how_to':
      return <HowToSection section={section} />;
    // 官方效果展示：左右分栏 / 叠加卡片（内部分发）
    case 'official_showcase':
      return <OfficialShowcaseSection section={section} />;
    case 'use_case_intro':
      switch (section.layout) {
        case 'tab_preview':
          return <UseCaseIntroTabPreview section={section} />;
        default:
          return <UseCaseIntroAlternating section={section} />;
      }
    // 用户案例：轮播(default) / 瀑布流
    case 'user_cases':
      switch (section.layout) {
        case 'masonry':
          return <UserCasesMasonry section={section} />;
        default:
          return <UserCasesCarousel section={section} />;
      }
    // 分类 Tab：标签切换(default) / 手风琴面板
    case 'category_tabs':
      switch (section.layout) {
        case 'accordion':
          return <CategoryTabsAccordion section={section} />;
        default:
          return <CategoryTabsSwitch section={section} />;
      }
    // 用途场景卡片：卡片网格(default) / 分栏列表
    case 'use_case_cards':
      switch (section.layout) {
        case 'split_list':
          return <UseCaseCardsSplitList section={section} />;
        default:
          return <UseCaseCardsGrid section={section} />;
      }
    // 卖点展示：卡片网格 / 居中图标（内部判断 layout）
    case 'feature_highlights':
      return <FeatureHighlightsSection section={section} />;
    // 相关功能推荐
    case 'related_features':
      return <RelatedFeaturesSection section={section} />;
    // 相关场景跳转
    case 'related_scenarios':
      return <RelatedScenariosSection section={section} />;
    // 用户背书
    case 'testimonials':
      return <TestimonialsSection section={section} />;
    // 对比表格
    case 'comparison_table':
      return <ComparisonTableSection section={section} />;
    // 提示词公式
    case 'prompt_formula':
      return <PromptFormulaSection section={section} />;
    // FAQ
    case 'faq':
      return <FAQSection section={section} />;
    // 结尾 CTA
    case 'closing_cta':
      return <ClosingCTASection section={section} />;
    default:
      return null;
  }
}

export function ToolsSeoPage({ page }: { page: ToolPageData }) {
  return (
    <>
      {page.sections.map((section, index) => (
        <Fragment key={section._key || `${section._type}-${index}`}>{renderSection(section)}</Fragment>
      ))}
    </>
  );
}
